/**
 * Service for fetching user AD groups from backend API
 */

import { API_BASE_URL, apiPath } from '../config/env';

export interface UserGroup {
    id: string;
    displayName: string;
    description?: string;
    groupType?: string;
}

export interface UserGroupsServiceResult {
    groups: UserGroup[];
    groupNamesCommaSeparated: string;
}

export interface UserGroupsResponse {
    user: string;
    group_count: number;
    groups: {
        id: string;
        display_name: string;
    }[];
    error: string | null;
}

/**
 * Fetch user's groups from backend API and return both groups and comma-separated names
 */
export async function fetchUserGroups(userEmail: string): Promise<UserGroupsServiceResult> {
    try {
        const encodedEmail = encodeURIComponent(userEmail);
        const url = `${API_BASE_URL}${apiPath(`unity-catalog/user-groups?user_email=${encodedEmail}`)}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.error(`[UserGroups] Failed to fetch groups: ${response.status} ${response.statusText}`);
            return { groups: [], groupNamesCommaSeparated: '' };
        }

        const data: UserGroupsResponse = await response.json();

        if (data.error) {
            console.error(`[UserGroups] API returned error: ${data.error}`);
            return { groups: [], groupNamesCommaSeparated: '' };
        }

        // Transform API response to our UserGroup format
        const groups: UserGroup[] = data.groups.map(group => ({
            id: group.id,
            displayName: group.display_name
        }));

        // Create comma-separated list of group names for unity catalog APIs
        const groupNamesCommaSeparated = data.groups.map(g => g.display_name).join(',');

        return { groups, groupNamesCommaSeparated };
    } catch (error) {
        console.error('[UserGroups] Error fetching user groups:', error);
        return { groups: [], groupNamesCommaSeparated: '' };
    }
}